import React, { useMemo, useState, useEffect, useRef, useCallback, useContext } from 'react';
import SEO from './components/SEO';
import {
  FaSearch,
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaExpand,
  FaChevronLeft,
  FaChevronRight,
  FaRandom,
  FaDice,
  FaRedoAlt,
  FaBolt,
  FaVolumeUp,
  FaVolumeMute,
  FaTimes,
} from 'react-icons/fa';
import YouTube from 'react-youtube';
import transliterateText from './utils/transliteration';
import playlistsData from './assets/livestreaming/lean/playlists-lean.json';
import playlistItemsMap from './assets/livestreaming/lean/playlist-items-lean.json';
import videosCatalog from './assets/livestreaming/lean/videos-lean.json';
import ActiveSongContext from './contexts/ActiveSongContext';
import './audio.css';

const selectImageFromThumbnails = (thumbs = {}) =>
  thumbs.maxres?.url || thumbs.standard?.url || thumbs.high?.url || thumbs.medium?.url || thumbs.default?.url || '';

const resolvePlaylistEntryId = (entry) => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  return entry.id || entry.videoId || '';
};

const buildPlaylistLookup = (items) => {
  const lookup = {};
  Object.entries(items || {}).forEach(([playlistId, entries]) => {
    const ids = new Set();
    (entries || []).forEach((entry) => {
      const id = resolvePlaylistEntryId(entry);
      if (id) ids.add(id);
    });
    lookup[playlistId] = ids;
  });
  return lookup;
};

const buildSearchableText = (...values) => {
  const combined = values
    .filter(Boolean)
    .map((value) => String(value))
    .join(' ');
  const latinized = transliterateText(combined);
  return `${combined} ${latinized}`.toLowerCase();
};

const parseDuration = (value) => {
  if (!value) return 0;
  const matches = value.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!matches) return 0;
  const [, hours, minutes, seconds] = matches;
  return (Number(hours) || 0) * 3600 + (Number(minutes) || 0) * 60 + (Number(seconds) || 0);
};

const formatTime = (seconds = 0) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = Math.floor(safeSeconds % 60);
  if (hours) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed);
};

const parseBooleanParam = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return fallback;
};

const shuffleList = (items = []) => {
  const array = [...items];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

export default function Audio() {
  const { setActiveSongTitle } = useContext(ActiveSongContext);
  const defaultAutoNext = true;
  const [autoPlayNext, setAutoPlayNext] = useState(() => {
    if (typeof window === 'undefined') return defaultAutoNext;
    const params = new URLSearchParams(window.location.search || '');
    if (!params.has('autonext')) return defaultAutoNext;
    return parseBooleanParam(params.get('autonext'), defaultAutoNext);
  });
  const videoEntries = useMemo(() => {
    return Object.values(videosCatalog || {})
      .map((video) => {
        const entry = {
          id: video.id,
          title: video.title || 'Untitled track',
          channelTitle: video.channelTitle || 'GCC Worship Platform',
          thumbnails: video.thumbnails || {},
          publishedAt: video.publishedAt,
          duration: video.duration,
          tags: Array.isArray(video.tags) ? video.tags : [],
        };
        return {
          ...entry,
          durationSeconds: parseDuration(entry.duration),
          image: selectImageFromThumbnails(entry.thumbnails),
          searchText: buildSearchableText(entry.title, entry.channelTitle, entry.tags.join(' ')),
        };
      })
      .sort((a, b) => {
        const left = Date.parse(a.publishedAt) || 0;
        const right = Date.parse(b.publishedAt) || 0;
        return right - left;
      });
  }, []);

  const playlistEntries = useMemo(() => {
    return (playlistsData || []).map((playlist) => ({
      ...playlist,
      searchText: buildSearchableText(playlist.title, playlist.channelTitle),
      image: selectImageFromThumbnails(playlist.thumbnails || {}),
      count: playlist.videoIds?.length || playlist.itemCount || 0,
    }));
  }, []);

  const playlistLookup = useMemo(() => buildPlaylistLookup(playlistItemsMap), []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search || '');
    return params.get('playlist') || '';
  });
  const [playerVideoId, setPlayerVideoId] = useState(videoEntries[0]?.id || '');
  const [playerState, setPlayerState] = useState(-1);
  const [queueIndex, setQueueIndex] = useState(0);
  const [pendingPlayId, setPendingPlayId] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const playerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileTab, setMobileTab] = useState('queue');
  const [isPlaylistCollapsed, setIsPlaylistCollapsed] = useState(false);
  const [isQueueCollapsed, setIsQueueCollapsed] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off');
  const [smartShuffle, setSmartShuffle] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [bottomPanelOpen, setBottomPanelOpen] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [needsUserPlay, setNeedsUserPlay] = useState(false);
  const statusTimerRef = useRef(null);
  const userGestureRef = useRef(false);
  const pendingAutoPlayIdRef = useRef(null);

  const showStatus = useCallback((message) => {
    setStatusMessage(message);
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
    }
    statusTimerRef.current = setTimeout(() => {
      setStatusMessage('');
    }, 3200);
  }, []);

  const markUserGesture = useCallback(() => {
    userGestureRef.current = true;
  }, []);

  const ensureMobileAutoplay = useCallback((player) => {
    if (!isMobileView) return;
  }, [isMobileView]);

  const scheduleAutoplayFallbackCheck = useCallback((videoId) => {
    if (!videoId) return;
    const player = playerRef.current;
    if (!player || typeof player.getPlayerState !== 'function') return;
    pendingAutoPlayIdRef.current = videoId;
    window.setTimeout(() => {
      if (pendingAutoPlayIdRef.current !== videoId) return;
      const state = player.getPlayerState();
      if (state !== 1) {
        setNeedsUserPlay(true);
      }
    }, 700);
  }, []);

  useEffect(() => {
    if (selectedPlaylistId || !playlistEntries.length) return;
    const randomIndex = Math.floor(Math.random() * playlistEntries.length);
    const randomPlaylistId = playlistEntries[randomIndex]?.id || '';
    if (randomPlaylistId) {
      setSelectedPlaylistId(randomPlaylistId);
    }
  }, [playlistEntries, selectedPlaylistId]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const transliteratedSearch = normalizedSearch ? transliterateText(normalizedSearch) : '';

  const filteredPlaylists = useMemo(() => {
    if (!normalizedSearch) return playlistEntries;
    return playlistEntries.filter((playlist) => {
      const haystack = playlist.searchText;
      return (
        (normalizedSearch && haystack.includes(normalizedSearch)) ||
        (transliteratedSearch && haystack.includes(transliteratedSearch))
      );
    });
  }, [playlistEntries, normalizedSearch, transliteratedSearch]);

  const filteredAudios = useMemo(() => {
    let queue = videoEntries;
    if (selectedPlaylistId && playlistLookup[selectedPlaylistId]) {
      const playlistSet = playlistLookup[selectedPlaylistId];
      queue = queue.filter((track) => playlistSet.has(track.id));
    }
    if (normalizedSearch) {
      queue = queue.filter((track) => {
        const haystack = track.searchText;
        return (
          (normalizedSearch && haystack.includes(normalizedSearch)) ||
          (transliteratedSearch && haystack.includes(transliteratedSearch))
        );
      });
    }
    return queue;
  }, [normalizedSearch, playlistLookup, selectedPlaylistId, transliteratedSearch, videoEntries]);

  const visibleAudios = useMemo(() => {
    if (!filteredAudios.length) return [];
    return filteredAudios;
  }, [filteredAudios]);

  const activeQueue = useMemo(() => {
    if (!visibleAudios.length) return [];
    if (smartShuffle) {
      return shuffleList(visibleAudios);
    }
    if (shuffleEnabled) {
      return shuffleList(visibleAudios);
    }
    return visibleAudios;
  }, [visibleAudios, shuffleEnabled, smartShuffle]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const checkSize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  useEffect(() => {
    if (!isMobileView) {
      setMobileTab('queue');
    }
  }, [isMobileView]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (autoPlayNext === defaultAutoNext) {
      params.delete('autonext');
    } else {
      params.set('autonext', autoPlayNext ? '1' : '0');
    }
    const nextSearch = params.toString();
    const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }, [autoPlayNext, defaultAutoNext]);

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const headerLogo = document.querySelector('.header-logo');
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = 'pointer';
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setControlsOpen((prev) => !prev);
      setBottomPanelOpen((prev) => !prev);
    };
    headerLogo.addEventListener('click', handleHeaderClick);
    return () => {
      headerLogo.removeEventListener('click', handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyToggle = (event) => {
      if (event.key === 'Control' && !event.repeat && !event.altKey && !event.metaKey) {
        setControlsOpen((prev) => !prev);
        setBottomPanelOpen((prev) => !prev);
        return;
      }
      if (event.key === 'Escape' && controlsOpen) {
        setControlsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyToggle);
    return () => window.removeEventListener('keydown', handleKeyToggle);
  }, [controlsOpen]);

  const queueAudio = useCallback(
    (index) => {
      if (!activeQueue.length) {
        setPlayerVideoId('');
        setQueueIndex(0);
        return;
      }
      const boundedIndex = Math.max(0, Math.min(activeQueue.length - 1, index));
      const nextTrack = activeQueue[boundedIndex];
      if (!nextTrack) return;
      setQueueIndex(boundedIndex);
      setPlayerVideoId(nextTrack.id);
      setPendingPlayId(nextTrack.id);
    },
    [activeQueue],
  );

  const getRandomQueueIndex = useCallback((length, excludeIndex) => {
    if (length <= 1) return 0;
    let nextIndex = excludeIndex;
    let guard = 0;
    while (nextIndex === excludeIndex && guard < 8) {
      nextIndex = Math.floor(Math.random() * length);
      guard += 1;
    }
    if (nextIndex === excludeIndex) {
      return (excludeIndex + 1) % length;
    }
    return nextIndex;
  }, []);

  const handleNext = useCallback(() => {
    if (!activeQueue.length) return;
    if (repeatMode === 'one') {
      queueAudio(queueIndex);
      return;
    }
    const nextIndex = randomPlayNext
      ? getRandomQueueIndex(activeQueue.length, queueIndex)
      : (queueIndex + 1) % activeQueue.length;
    queueAudio(nextIndex);
  }, [activeQueue.length, queueAudio, queueIndex, repeatMode, randomPlayNext, getRandomQueueIndex]);

  const handlePrevious = useCallback(() => {
    if (!activeQueue.length) return;
    const prevIndex = (queueIndex - 1 + activeQueue.length) % activeQueue.length;
    queueAudio(prevIndex);
  }, [activeQueue.length, queueAudio, queueIndex]);

  const attemptPlay = useCallback((player) => {
    if (!player || typeof player.playVideo !== 'function') return;
    ensureMobileAutoplay(player);
    const tryPlay = (attempt = 0) => {
      try {
        player.playVideo();
      } catch (error) {
        return;
      }
      if (attempt >= 2) return;
      const delay = attempt === 0 ? 200 : 600;
      window.setTimeout(() => {
        let state = null;
        try {
          state = typeof player.getPlayerState === 'function' ? player.getPlayerState() : null;
        } catch (error) {
          return;
        }
        if (state !== 1) {
          tryPlay(attempt + 1);
        }
      }, delay);
    };
    tryPlay();
  }, []);

  const playQueuedTrack = useCallback((trackId) => {
    if (!trackId) return;
    const player = playerRef.current;
    if (!canSendPlayerCommands(player)) return;
    if (typeof player.loadVideoById === 'function') {
      player.loadVideoById(trackId);
    }
    attemptPlay(player);
  }, [attemptPlay]);

  const handleAutoAdvance = useCallback(() => {
    if (!autoPlayNext || !activeQueue.length) return;
    const nextIndex = repeatMode === 'one'
      ? queueIndex
      : randomPlayNext
        ? getRandomQueueIndex(activeQueue.length, queueIndex)
        : (queueIndex + 1) % activeQueue.length;
    const nextTrack = activeQueue[nextIndex];
    setNeedsUserPlay(false);
    queueAudio(nextIndex);
    playQueuedTrack(nextTrack?.id);
    scheduleAutoplayFallbackCheck(nextTrack?.id);
  }, [
    activeQueue,
    autoPlayNext,
    scheduleAutoplayFallbackCheck,
    queueAudio,
    queueIndex,
    repeatMode,
    playQueuedTrack,
    randomPlayNext,
    getRandomQueueIndex,
  ]);

  const handlePlayerReady = useCallback((event) => {
    playerRef.current = event.target;
    setPlayerReady(true);
    const trackDuration = event.target.getDuration();
    if (trackDuration) {
      setDuration(trackDuration);
    }
  }, []);

  const startProgressTimer = useCallback(() => {
    if (progressTimerRef.current) return;
    progressTimerRef.current = window.setInterval(() => {
      const player = playerRef.current;
      if (!player || typeof player.getCurrentTime !== 'function') return;
      const currentTime = Math.max(0, player.getCurrentTime());
      setProgress(currentTime);
      const trackDuration = player.getDuration();
      if (typeof trackDuration === 'number' && !Number.isNaN(trackDuration)) {
        setDuration(trackDuration);
      }
    }, 500);
  }, []);

  const stopProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const handlePlayerStateChange = useCallback(
    (event) => {
      const state = event.data;
      setPlayerState(state);
      if (state === 1) {
        setNeedsUserPlay(false);
        pendingAutoPlayIdRef.current = null;
      }
      if (state === 1 && pendingPlayId) {
        setPendingPlayId(null);
      }
      if (state === 5 && pendingPlayId) {
        const player = playerRef.current;
        if (canSendPlayerCommands(player)) {
          attemptPlay(player);
        }
      }
      if (state === 1) {
        startProgressTimer();
      } else {
        stopProgressTimer();
      }
      if (state === 0) {
        handleAutoAdvance();
      }
    },
    [attemptPlay, handleAutoAdvance, pendingPlayId, startProgressTimer, stopProgressTimer],
  );

  const handleSeek = (event) => {
    const value = Number(event.target.value) || 0;
    setProgress(value);
    const player = playerRef.current;
    if (player && typeof player.seekTo === 'function') {
      player.seekTo(value, true);
    }
  };

  const handlePlayPause = () => {
    markUserGesture();
    const player = playerRef.current;
    if (!player) return;
    if (playerState === 1) {
      player.pauseVideo();
    } else {
      ensureMobileAutoplay(player);
      player.playVideo();
    }
  };

  const handleResumeAutoplay = () => {
    const player = playerRef.current;
    if (!player) return;
    markUserGesture();
    const nextId = pendingAutoPlayIdRef.current || playerVideoId;
    if (nextId && typeof player.loadVideoById === 'function') {
      player.loadVideoById(nextId);
    }
    player.unMute?.();
    player.setVolume?.(volume);
    setIsMuted(false);
    player.playVideo();
    setNeedsUserPlay(false);
    pendingAutoPlayIdRef.current = null;
  };

  useEffect(() => {
    if (!activeQueue.length) {
      setPlayerVideoId('');
      setQueueIndex(0);
      return;
    }
    if (!playerVideoId) {
      queueAudio(0);
      return;
    }
    const matchIndex = activeQueue.findIndex((track) => track.id === playerVideoId);
    if (matchIndex >= 0 && matchIndex !== queueIndex) {
      setQueueIndex(matchIndex);
      return;
    }
    if (matchIndex < 0) {
      queueAudio(0);
    }
  }, [activeQueue, playerVideoId, queueIndex, queueAudio]);

  useEffect(() => {
    setProgress(0);
  }, [playerVideoId]);

  const canSendPlayerCommands = (player) => {
    if (!player) return false;
    if (typeof player.playVideo !== 'function') return false;
    if (typeof player.getIframe !== 'function') return false;
    return Boolean(player.getIframe());
  };

  useEffect(() => {
    if (!pendingPlayId || !playerReady) return;
    const player = playerRef.current;
    if (!canSendPlayerCommands(player)) return;
    const currentId = typeof player.getVideoData === 'function' ? player.getVideoData()?.video_id : null;
    if (pendingPlayId && pendingPlayId !== currentId && typeof player.loadVideoById === 'function') {
      player.loadVideoById(pendingPlayId);
    }
    attemptPlay(player);
  }, [pendingPlayId, playerReady, attemptPlay]);

  useEffect(() => {
    if (!playerVideoId) return;
    scheduleAutoplayFallbackCheck(playerVideoId);
  }, [playerVideoId, scheduleAutoplayFallbackCheck]);

  useEffect(() => {
    return () => {
      stopProgressTimer();
    };
  }, [stopProgressTimer]);

  const videoEntryMap = useMemo(() => {
    const map = {};
    videoEntries.forEach((track) => {
      if (track?.id) {
        map[track.id] = track;
      }
    });
    return map;
  }, [videoEntries]);

  const currentTrack = useMemo(
    () => videoEntryMap[playerVideoId] || null,
    [playerVideoId, videoEntryMap],
  );

  useEffect(() => {
    setActiveSongTitle(currentTrack?.title || '');
  }, [currentTrack, setActiveSongTitle]);

  useEffect(() => () => setActiveSongTitle(''), [setActiveSongTitle]);

  const selectedPlaylistTracks = useMemo(() => {
    if (!selectedPlaylistId) return [];
    const entries = playlistItemsMap?.[selectedPlaylistId] || [];
    return entries
      .map((entry) => videoEntryMap[resolvePlaylistEntryId(entry)] || null)
      .filter(Boolean);
  }, [selectedPlaylistId, videoEntryMap]);

  const currentPlaylist = useMemo(
    () => playlistEntries.find((playlist) => playlist.id === selectedPlaylistId) || null,
    [playlistEntries, selectedPlaylistId],
  );

  const showPlaylistSection = !isMobileView || mobileTab === 'playlists';
  const showQueueSection = !isMobileView || mobileTab === 'queue';
  const gridClasses = ['audio-grid'];
  if (!showPlaylistSection || !showQueueSection) {
    gridClasses.push('audio-grid--stacked');
  }
  if (isPlaylistCollapsed && showPlaylistSection && showQueueSection) {
    gridClasses.push('audio-grid--playlist-collapsed');
  }
  if (isQueueCollapsed && showPlaylistSection && showQueueSection) {
    gridClasses.push('audio-grid--queue-collapsed');
  }

  const handleFullscreen = useCallback(async () => {
    if (typeof document === 'undefined') return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      } else {
        await document.documentElement.requestFullscreen?.();
      }
    } catch {
      // ignore fullscreen failures
    }
  }, []);

  const hasFilters = Boolean(normalizedSearch || selectedPlaylistId);
  const trackDuration = Math.max(duration, currentTrack?.durationSeconds || 0);
  const sliderMax = Math.max(trackDuration, 1);
  const sliderValue = Math.min(progress, sliderMax);

  const toggleShuffle = useCallback(() => {
    setShuffleEnabled((prev) => {
      const next = !prev;
      showStatus(next ? 'Shuffle enabled' : 'Shuffle disabled');
      return next;
    });
  }, [showStatus]);

  const cycleRepeatMode = useCallback(() => {
    setRepeatMode((prev) => {
      const next = prev === 'off' ? 'all' : prev === 'all' ? 'one' : 'off';
      const label = next === 'one' ? 'Repeat one' : next === 'all' ? 'Repeat all' : 'Repeat off';
      showStatus(label);
      return next;
    });
  }, [showStatus]);

  const toggleSmartShuffle = useCallback(() => {
    setSmartShuffle((prev) => {
      const next = !prev;
      showStatus(next ? 'Smart shuffle active' : 'Smart shuffle off');
      return next;
    });
  }, [showStatus]);

  const toggleMute = useCallback(() => {
    markUserGesture();
    setIsMuted((prev) => {
      const next = !prev;
      if (playerRef.current) {
        if (next) {
          playerRef.current.mute();
        } else {
          playerRef.current.unMute?.();
          playerRef.current.setVolume?.(volume);
        }
      }
      showStatus(next ? 'Muted' : 'Unmuted');
      return next;
    });
  }, [showStatus, volume]);

  const handleVolumeChange = useCallback(
    (event) => {
      markUserGesture();
      const next = Number(event.target.value) || 0;
      setVolume(next);
      if (!isMuted && playerRef.current && typeof playerRef.current.setVolume === 'function') {
        playerRef.current.setVolume(next);
      }
    },
    [isMuted],
  );

  useEffect(() => {
    const player = playerRef.current;
    if (!player || typeof player.setVolume !== 'function') return;
    if (isMuted) {
      player.mute();
      player.setVolume(0);
    } else {
      player.unMute?.();
      player.setVolume(volume);
    }
  }, [isMuted, volume]);

  return (
    <div className="audio-page">
      <SEO
        title="Audio"
        description="Listen to music and messages with queue and playback controls."
      />
      <div className="audio-tabs-row">
        <div className="audio-mobile-tabs">
          <button
            type="button"
            className={`audio-mobile-tab ${mobileTab === 'playlists' ? 'active' : ''}`}
            onClick={() => setMobileTab('playlists')}
          >
            Playlists
          </button>
          <button
            type="button"
            className={`audio-mobile-tab ${mobileTab === 'queue' ? 'active' : ''}`}
            onClick={() => setMobileTab('queue')}
          >
            Queue
          </button>
        </div>
      </div>

      <div className={`audio-top-controls${controlsOpen ? ' open' : ''}`}>
        <div className="audio-top-controls-inner">
          <div className="audio-top-controls-header">
            <span>Playback controls</span>
            <button
              type="button"
              className="audio-top-controls-close"
              onClick={() => setControlsOpen(false)}
              aria-label="Close controls"
            >
              <FaTimes />
            </button>
          </div>
          <div className="audio-extra-controls">
            <button
              type="button"
              className={`control-button${autoPlayNext ? ' active' : ''}`}
              onClick={() => setAutoPlayNext((prev) => !prev)}
              aria-pressed={autoPlayNext}
              aria-label="Toggle auto play next"
              title="Auto next"
            >
              <FaStepForward />
            </button>
            <button
              type="button"
              className={`control-button${randomPlayNext ? ' active' : ''}`}
              onClick={() => setRandomPlayNext((prev) => !prev)}
              aria-pressed={randomPlayNext}
              aria-label="Toggle random play"
              title="Random"
            >
              <FaDice />
            </button>
            <button
              type="button"
              className={`control-button${shuffleEnabled ? ' active' : ''}`}
              onClick={toggleShuffle}
              aria-label="Toggle shuffle"
              title="Shuffle"
            >
              <FaRandom />
            </button>
            <button
              type="button"
              className={`control-button${repeatMode !== 'off' ? ' active' : ''}`}
              onClick={cycleRepeatMode}
              aria-label="Cycle repeat mode"
              title={
                repeatMode === 'one'
                  ? 'Repeat one'
                  : repeatMode === 'all'
                    ? 'Repeat all'
                    : 'Repeat'
              }
            >
              <FaRedoAlt />
            </button>
            <button
              type="button"
              className={`control-button${smartShuffle ? ' active' : ''}`}
              onClick={toggleSmartShuffle}
              aria-label="Smart shuffle"
              title="Smart shuffle"
            >
              <FaBolt />
            </button>
          </div>
          <div className="audio-extra-controls secondary">
            <div className="audio-volume-row">
              <button type="button" className="volume-toggle" onClick={toggleMute} aria-label="Toggle mute">
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={volume}
                onChange={handleVolumeChange}
                aria-label="Adjust volume"
              />
            </div>
          </div>
          {statusMessage && <div className="audio-status-chip">{statusMessage}</div>}
        </div>
      </div>

      <div className={gridClasses.join(' ')}>
        {showPlaylistSection && (
          <section className={`audio-playlist-panel ${isPlaylistCollapsed ? 'collapsed-panel' : ''}`}>
            <header className="audio-panel-header">
              <h2>Playlists</h2>
              <button
                type="button"
                className="panel-collapse-toggle"
                onClick={() => setIsPlaylistCollapsed((prev) => !prev)}
                aria-label={isPlaylistCollapsed ? 'Expand playlists panel' : 'Collapse playlists panel'}
              >
                {isPlaylistCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
              </button>
            </header>
            <div className={`panel-body ${isPlaylistCollapsed ? 'collapsed' : ''}`}>
              <div className="audio-playlist-list">
                {filteredPlaylists.map((playlist) => (
                  <button
                    key={playlist.id}
                    type="button"
                    className={`audio-playlist-card ${selectedPlaylistId === playlist.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedPlaylistId((current) => (current === playlist.id ? '' : playlist.id));
                      if (isMobileView) {
                        setMobileTab('queue');
                      }
                    }}
                  >
                    <div
                      className="audio-playlist-thumb"
                      style={{
                        backgroundImage: `linear-gradient(145deg, rgba(0,0,0,0.65), rgba(0,0,0,0)), url(${playlist.image})`,
                      }}
                    />
                    <div className="audio-playlist-info">
                      <strong>{playlist.title}</strong>
                      <span>
                        {playlist.count || 0} {playlist.count === 1 ? 'track' : 'tracks'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}
        {showQueueSection && (
          <section className={`audio-main-panel ${isQueueCollapsed ? 'collapsed-panel' : ''}`}>
            <header className="audio-panel-header">
              <div>
                <h2>Queue</h2>
              </div>
              <div className="panel-header-actions">
                <span className="audio-queue-count">
                  {visibleAudios.length} {visibleAudios.length === 1 ? 'track' : 'tracks'}
                </span>
                {hasFilters && (
                  <button
                    type="button"
                    className="audio-clear-filters"
                    onClick={() => {
                      setSelectedPlaylistId('');
                      setSearchQuery('');
                    }}
                  >
                    Clear filters
                  </button>
                )}
                <button
                  type="button"
                  className="panel-collapse-toggle"
                  onClick={() => setIsQueueCollapsed((prev) => !prev)}
                  aria-label={isQueueCollapsed ? 'Expand queue panel' : 'Collapse queue panel'}
                >
                  {isQueueCollapsed ? <FaChevronLeft /> : <FaChevronRight />}
                </button>
              </div>
            </header>
            <div className={`panel-body ${isQueueCollapsed ? 'collapsed' : ''}`}>
              <div className="audio-queue-list">
                {activeQueue.length ? (
                  activeQueue.map((track, index) => (
                    <button
                      key={track.id}
                      type="button"
                      className={`audio-track-row ${playerVideoId === track.id ? 'active' : ''}`}
                      onClick={() => queueAudio(index)}
                    >
                      <span className="audio-track-index">{index + 1}</span>
                      <div className="audio-track-thumb" style={{ backgroundImage: `url(${track.image})` }} />
                      <div className="audio-track-meta">
                        <strong>{track.title}</strong>
                        <small>
                          {track.channelTitle} · {formatDate(track.publishedAt)}
                        </small>
                      </div>
                      <span className="audio-track-duration">
                        {track.durationSeconds
                          ? formatTime(track.durationSeconds)
                          : formatTime(trackDuration)}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="audio-empty-state">
                    <p>No tracks match your filters yet. Try a different playlist or clear search.</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      {bottomPanelOpen && (
        <div className="audio-player-bar">
          <div className="audio-player-filters">
            <div className="audio-search-bar">
              <FaSearch />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search playlists, tracks, lyrics..."
              />
            </div>
            <div className="audio-playlist-dropdown">
              <select
                value={selectedPlaylistId}
                onChange={(event) => {
                  setSelectedPlaylistId(event.target.value);
                  if (isMobileView) {
                    setMobileTab('queue');
                  }
                }}
                aria-label="Filter by playlist"
              >
                <option value="">All playlists</option>
                {playlistEntries.map((playlist) => (
                  <option key={playlist.id} value={playlist.id}>
                    {playlist.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="audio-player-info">
            <div
              className="audio-player-thumb"
              style={{ backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.8), rgba(0,0,0,0)), url(${currentTrack?.image})` }}
            />
          <div className="audio-player-details">
            <strong>{currentTrack?.title || 'Pick a track to begin'}</strong>
          </div>
          </div>
          <div className="audio-player-controls">
            <button
              type="button"
              className="audio-control"
              onClick={() => {
                markUserGesture();
                handlePrevious();
              }}
              aria-label="Previous track"
            >
              <FaStepBackward />
            </button>
            <button
              type="button"
              className="audio-control play-toggle"
              onClick={handlePlayPause}
              aria-label="Play or pause"
            >
              {playerState === 1 ? <FaPause /> : <FaPlay />}
            </button>
            <button
              type="button"
              className="audio-control"
              onClick={() => {
                markUserGesture();
                handleNext();
              }}
              aria-label="Next track"
            >
              <FaStepForward />
            </button>
            {needsUserPlay && (
              <button
                type="button"
                className="audio-control resume-control"
                onClick={handleResumeAutoplay}
                aria-label="Tap to play next"
              >
                Tap to play next
              </button>
            )}
            <button type="button" className="audio-control" onClick={handleFullscreen} aria-label="Toggle fullscreen">
              <FaExpand />
            </button>
            <button
              type="button"
              className="audio-control panel-close"
              onClick={() => setBottomPanelOpen(false)}
              aria-label="Hide bottom controls"
            >
              <FaTimes />
            </button>
          </div>
          <div className="audio-player-progress">
            <span>{formatTime(sliderValue)}</span>
            <input
              type="range"
              className="audio-progress-input"
              min={0}
              max={sliderMax}
              value={sliderValue}
              onChange={handleSeek}
              aria-label="Seek audio"
            />
            <span>{formatTime(trackDuration)}</span>
          </div>
        </div>
      )}

      <div className="audio-hidden-player">
        {playerVideoId && (
          <YouTube
            videoId={playerVideoId}
            opts={{
              width: '0',
              height: '0',
              playerVars: {
                autoplay: 1,
                controls: 0,
                rel: 0,
                modestbranding: 1,
                disablekb: 1,
                playsinline: 1,
              },
            }}
            onReady={handlePlayerReady}
            onStateChange={handlePlayerStateChange}
            onEnd={handleAutoAdvance}
            className="audio-hidden-iframe"
          />
        )}
      </div>
    </div>
  );
}
